import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { BrowserRouter, Routes, Route, Navigate, NavLink } from 'react-router-dom';
import GmailSignatureGenerator from './components/GmailSignatureGenerator';
import RoleplayApp from './components/roleplay/RoleplayApp';
import Dashboard from './pages/Dashboard';
import PageView from './pages/PageView';
import Admin from './pages/Admin';
import { onAuthStateChanged, signInWithGoogle, signOutUser, getRedirectResult } from './firebase';
import { isAdminEmail } from './config/admins';
import { subscribeAdmins } from './data/admins';

const AppContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding-top: 60px;
`;

const Header = styled.header`
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 12px 0;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Logo = styled.img`
  width: 148px;
`;

const AppTitle = styled.span`
  color: #1e293b;
  font-size: 16px;
  font-weight: 600;
`;

const ContentContainer = styled.div`
  padding: 0;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 48px 40px;
  max-width: 440px;
  width: 90%;
  box-shadow: 0 10px 32px rgba(0,0,0,0.08);
  text-align: center;
  margin: 20px;
`;

const LoginTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #1e293b;
  margin: 24px 0 8px 0;
  line-height: 1.2;
`;

const LoginSubtitle = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0 0 32px 0;
  line-height: 1.5;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin: 0 0 24px 0;
`;

const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 14px 20px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 10px;
  border: 1px solid #dadce0;
  background: #ffffff;
  color: #3c4043;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    border-color: #c6c6c6;
  }
  
  &:active {
    transform: translateY(1px);
  }
`;

const GoogleIcon = styled.div`
  width: 20px;
  height: 20px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%234285F4' d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'/%3E%3Cpath fill='%2334A853' d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'/%3E%3Cpath fill='%23FBBC05' d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'/%3E%3Cpath fill='%23EA4335' d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'/%3E%3C/svg%3E") no-repeat center;
  background-size: contain;
`;

const Button = styled.button`
  padding: 10px 14px;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid #0e8c6a;
  background: #0e8c6a;
  color: #fff;
  cursor: pointer;
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 16px;
`;

const NavItem = styled(NavLink)`
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  text-decoration: none;
  transition: all 0.15s ease;

  &:hover {
    color: #1e293b;
    background: #f1f5f9;
  }

  &.active {
    color: #0e8c6a;
    background: #e6f5f0;
  }
`;

const UserAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid #e2e8f0;
  object-fit: cover;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const UserName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.2;
`;

const UserEmail = styled.span`
  font-size: 12px;
  color: #64748b;
  line-height: 1.2;
`;

function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [msg, setMsg] = useState('');
  const [adminEmails, setAdminEmails] = useState([]);
  const allowedDomain = 'meltplan.com';

  useEffect(() => {
    // Handle the redirect result to surface errors or non-allowed domains
    getRedirectResult()
      .then(async (result) => {
        if (result && result.user) {
          const email = (result.user.email || '').toLowerCase();
          if (!email.endsWith(`@${allowedDomain}`)) {
            setMsg('Please sign in with your @meltplan.com account.');
            try { await signOutUser(); } catch {}
          }
        }
      })
      .catch((err) => {
        setMsg(err?.message || 'Sign-in failed. Please try again.');
      });
  
    const unsub = onAuthStateChanged((u) => {
      if (u && u.email && u.email.toLowerCase().endsWith(`@${allowedDomain}`)) {
        setUser(u);
      } else {
        setUser(null);
      }
      setChecking(false);
    });
    return () => unsub();
  }, []);

  // Subscribe to the runtime admin allowlist (only once signed in).
  useEffect(() => {
    if (!user) {
      setAdminEmails([]);
      return undefined;
    }
    const unsub = subscribeAdmins((rows) => setAdminEmails(rows.map((r) => r.email)));
    return () => unsub();
  }, [user]);

  const SignInView = (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <Card>
        <Logo src="/logo.png" alt="Meltplan Logo" style={{ width: 140, marginBottom: 8 }} />
        <LoginTitle>Sign in to continue</LoginTitle>
        <LoginSubtitle>Only users with <strong>@{allowedDomain}</strong> email can access.</LoginSubtitle>
        {msg && <ErrorMessage>{msg}</ErrorMessage>}
        <GoogleButton onClick={signInWithGoogle}>
          <GoogleIcon />
          Continue with Google
        </GoogleButton>
      </Card>
    </div>
  );

  // Show loading state
  if (checking) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#64748b' }}>Loading...</div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!user) {
    return SignInView;
  }

  // Show main app if authenticated
  const admin = isAdminEmail(user?.email, adminEmails);

  return (
    <BrowserRouter>
      <AppContainer>
        <Header>
          <HeaderContent>
            <Logo src="/logo.png" alt="Meltplan Logo" />
            <AppTitle>Meltplan Resources Hub</AppTitle>
            <Nav>
              <NavItem to="/" end>Dashboard</NavItem>
              {admin && <NavItem to="/admin">Admin</NavItem>}
            </Nav>
            <UserSection>
              {user?.photoURL && (
                <UserAvatar
                  src={user.photoURL}
                  alt={user.displayName || 'User'}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <UserInfo>
                <UserName>{user?.displayName || 'User'}</UserName>
                <UserEmail>{user?.email}</UserEmail>
              </UserInfo>
              <Button style={{ background: '#fff', color: '#0e8c6a' }} onClick={signOutUser}>
                Sign out
              </Button>
            </UserSection>
          </HeaderContent>
        </Header>

        <ContentContainer>
          <Routes>
            <Route path="/" element={<Dashboard user={user} />} />
            <Route path="/p/:id" element={<PageView />} />
            <Route path="/tools/email-signature" element={<GmailSignatureGenerator user={user} />} />
            <Route path="/tools/sdr-roleplay/*" element={<RoleplayApp user={user} />} />
            <Route path="/admin" element={<Admin user={user} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ContentContainer>
      </AppContainer>
    </BrowserRouter>
  );
}

export default App;
